export type AttritionOutcome = "archived" | "unaligned";

export type AttritionRationaleBreakdown = {
  rationale: string;
  count: number;
  sourceRationales: string[];
};

export type AttritionSummaryResponse = {
  window: string;
  windowPreset: string;
  anchorDate: string;
  dateStart: string;
  dateEnd: string;
  timezone: string;
  trendBucket: string;
  postAlignmentCohortCount: number;
  alignedCount: number;
  archivedCount: number;
  unalignedCount: number;
  pendingUnalignmentCount: number;
  archivedPct: number;
  unalignedPct: number;
  archivedRationales: AttritionRationaleBreakdown[];
  unalignedRationales: AttritionRationaleBreakdown[];
  generatedAt: string;
  source: string;
};

export type AttritionRecord = {
  carespaceId: string;
  carespaceUrl: string;
  carespaceName: string;
  clinic: string;
  outcome: string;
  hasPendingUnalignmentPaf: boolean;
  effectiveDate: string;
  reasonCategory: string;
  sourceRationale: string;
  activityTitle: string;
  narrative: string;
  recordedBy: string;
};

export type AttritionRecordsResponse = {
  dateStart: string;
  dateEnd: string;
  timezone: string;
  outcome: string;
  reason: string;
  totalCount: number;
  rows: AttritionRecord[];
  source: string;
};

export type AttritionTimeframe = {
  dateStart: string;
  dateEnd: string;
  timezone: string;
};

export type AttritionRecordsParams = AttritionTimeframe & {
  outcome: AttritionOutcome;
  reason?: string;
  limit?: number;
  offset?: number;
};

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isRationaleBreakdown(value: unknown): value is AttritionRationaleBreakdown[] {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        item &&
        typeof item === "object" &&
        typeof (item as AttritionRationaleBreakdown).rationale === "string" &&
        isFiniteNumber((item as AttritionRationaleBreakdown).count) &&
        Array.isArray((item as AttritionRationaleBreakdown).sourceRationales) &&
        (item as AttritionRationaleBreakdown).sourceRationales.every(
          (rationale) => typeof rationale === "string",
        ),
    )
  );
}

function isAttritionRecord(record: unknown): record is AttritionRecord {
  if (!record || typeof record !== "object") {
    return false;
  }
  const row = record as AttritionRecord;
  return (
    typeof row.carespaceId === "string" &&
    typeof row.carespaceUrl === "string" &&
    typeof row.carespaceName === "string" &&
    typeof row.clinic === "string" &&
    typeof row.outcome === "string" &&
    typeof row.hasPendingUnalignmentPaf === "boolean" &&
    typeof row.effectiveDate === "string" &&
    typeof row.reasonCategory === "string" &&
    typeof row.sourceRationale === "string" &&
    typeof row.activityTitle === "string" &&
    typeof row.narrative === "string" &&
    typeof row.recordedBy === "string"
  );
}

export function isAttritionSummaryResponse(value: unknown): value is AttritionSummaryResponse {
  if (!value || typeof value !== "object") {
    return false;
  }
  const data = value as Partial<AttritionSummaryResponse>;
  const countFields = [
    "postAlignmentCohortCount",
    "alignedCount",
    "archivedCount",
    "unalignedCount",
    "pendingUnalignmentCount",
    "archivedPct",
    "unalignedPct",
  ] as const;

  return (
    typeof data.source === "string" &&
    typeof data.generatedAt === "string" &&
    typeof data.dateStart === "string" &&
    typeof data.dateEnd === "string" &&
    countFields.every((field) => isFiniteNumber(data[field])) &&
    isRationaleBreakdown(data.archivedRationales) &&
    isRationaleBreakdown(data.unalignedRationales)
  );
}

export function isAttritionRecordsResponse(value: unknown): value is AttritionRecordsResponse {
  if (!value || typeof value !== "object") {
    return false;
  }
  const data = value as Partial<AttritionRecordsResponse>;
  return (
    isFiniteNumber(data.totalCount) &&
    Array.isArray(data.rows) &&
    data.rows.every(isAttritionRecord)
  );
}
